/**
 * Mooni-style "mock UI" cards used in the Process section:
 *  - ConversaMockup: editorial photo (voice/conversation) + chat overlay
 *  - CodeMockup: terminal/editor with syntax-highlighted lines + callouts (legacy)
 *  - ChartMockup: bar chart + chat bubble + KPI label
 */
import { Sparkles } from "lucide-react";
import brandConversa from "@/assets/lp/brand-conversa.jpg";

export function ConversaMockup() {
  return (
    <div className="relative rounded-2xl border border-brand-line bg-[#0E0A0A] overflow-hidden h-[260px]">
      <img
        src={brandConversa}
        alt="Mulher elegante em estúdio escuro com ondas de voz rosa — sua marca capturada por conversa"
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
      {/* blend the photo into the dark card */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E0A0A]/75 via-transparent to-transparent" />

      {/* conversation overlay */}
      <div className="absolute top-4 left-4 z-10 flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "linear-gradient(135deg, #BE9AFB, #A16BF7)" }}>
          <Sparkles className="w-3.5 h-3.5 text-[#0A0810]" />
        </div>
        <div className="px-3 py-2 rounded-2xl rounded-tl-md bg-[#14111A]/85 backdrop-blur-sm border border-brand-line text-[11.5px] font-ui text-brand-ink2 max-w-[230px]">
          Me conta: qual é a <span className="text-white font-medium">bandeira</span> da sua marca?
        </div>
      </div>
      <div className="absolute bottom-4 right-4 z-10 animate-float-y">
        <span className="callout-chip">tom de voz capturado ✓</span>
      </div>
    </div>
  );
}

export function CodeMockup() {
  return (
    <div className="relative rounded-2xl border border-brand-line bg-[#0E0A0A] p-5 overflow-hidden h-[260px]">
      {/* corner bloom */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-brand-rose/30 blur-3xl" />

      {/* window header */}
      <div className="flex items-center gap-1.5 mb-4 relative z-10">
        <span className="w-2 h-2 rounded-full bg-[#362E42]" />
        <span className="w-2 h-2 rounded-full bg-[#362E42]" />
        <span className="w-2 h-2 rounded-full bg-[#362E42]" />
        <span className="ml-2 text-[10.5px] font-mono text-brand-ink3">persona.valiosia.ts</span>
      </div>

      {/* code lines */}
      <div className="font-mono text-[12px] leading-[1.7] relative z-10 select-none">
        <Line n={1}><span className="tok-keyword">const</span> <span className="tok-fn">persona</span> = <span className="tok-keyword">await</span> <span className="tok-fn">valiosia</span>.entrevistar({"{"}</Line>
        <Line n={2}>{"  "}<span className="tok-string">bandeira</span>: <span className="tok-comment">// preenche por conversa</span></Line>
        <Line n={3}>{"  "}<span className="tok-string">tom_de_voz</span>: <span className="tok-comment">// captura linguagem real</span></Line>
        <Line n={4}>{"  "}<span className="tok-string">posicionamento</span>: <span className="tok-keyword">'inferido'</span></Line>
        <Line n={5}>{"})"}</Line>
        <Line n={6} muted><span className="tok-comment">// → 5 campos salvos · status: brand_persona_pronta</span></Line>
      </div>

      {/* floating callouts pointing at lines */}
      <div className="absolute top-[100px] right-6 z-20 animate-float-y">
        <span className="callout-chip">Maria</span>
      </div>
      <div className="absolute top-[148px] right-16 z-20" style={{ animationDelay: "0.4s" }}>
        <span className="callout-chip-dark callout-chip">você</span>
      </div>
    </div>
  );
}

function Line({ n, children, muted }) {
  return (
    <div className="flex gap-3">
      <span className="text-brand-ink3 w-4 inline-block text-right">{n}</span>
      <span className={muted ? "tok-comment" : "code-line-1"}>{children}</span>
    </div>
  );
}

export function ChartMockup() {
  // Bar heights (percent of card body)
  const bars = [25, 42, 38, 78, 52, 45, 60];
  return (
    <div className="relative rounded-2xl border border-brand-line bg-[#0E0A0A] p-5 overflow-hidden h-[260px]">
      <div className="absolute -bottom-10 -left-12 w-48 h-48 rounded-full bg-brand-rose/25 blur-3xl" />

      {/* mock chat bubble at top */}
      <div className="relative z-10 flex items-start gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #BE9AFB, #A16BF7)" }}>
          <Sparkles className="w-3.5 h-3.5 text-[#0A0810]" />
        </div>
        <div className="px-3 py-2 rounded-2xl rounded-tl-md bg-[#14111A] border border-brand-line text-[11.5px] font-ui text-brand-ink2 max-w-[200px]">
          <span className="text-white font-medium">ValiosIA</span> é rápida, estratégica e fala a sua língua.
        </div>
      </div>

      {/* KPI label */}
      <div className="relative z-10 mb-2 flex items-end gap-2">
        <span className="font-display text-[34px] text-white leading-none">3.2x</span>
        <span className="text-[11px] font-ui text-brand-ink2 mb-1">engajamento</span>
        <span className="callout-chip ml-auto -mt-3">+ROI</span>
      </div>

      {/* Bar chart */}
      <div className="relative z-10 flex items-end gap-2.5 h-[88px] mt-3">
        {bars.map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-md ${i === 3 ? "bar-shimmer" : "bg-brand-elevated"}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="relative z-10 mt-1 flex gap-2.5">
        {["S", "T", "Q", "Q", "S", "S", "D"].map((d, i) => (
          <span key={i} className="flex-1 text-center text-[9px] font-mono text-brand-ink3">{d}</span>
        ))}
      </div>
    </div>
  );
}
